import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Spin, Tag, Progress, message as antMessage } from 'antd';
import {
  SendOutlined,
  PaperClipOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckCircleFilled,
  LoadingOutlined,
  BranchesOutlined,
  FileTextOutlined,
  NodeIndexOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '../../../contexts/LanguageContext';
import BuildAppPlanCard from './BuildAppPlanCard';

const ACCEPT_IMAGES = 'image/png,image/jpeg,image/gif,image/webp';
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB

const PLAN_TYPE_CONFIG = {
  workflow: { color: '#7234CF', icon: <BranchesOutlined />, label: '工作流程' },
  eform: { color: '#1677ff', icon: <FileTextOutlined />, label: '電子表單' },
  metaflow: { color: '#6d28d9', icon: <NodeIndexOutlined />, label: 'Meta Flow' },
  'workflow+eform': { color: '#7234CF', icon: <BranchesOutlined />, label: '流程 + 表單' },
  'workflow+metaflow': { color: '#7234CF', icon: <NodeIndexOutlined />, label: '流程 + Meta Flow' },
};

const label = (t, key, fallback) => {
  const v = t(key);
  return (!v || v === key) ? fallback : v;
};

/* ── Markdown styles ── */
const mdComponents = {
  p: ({ children }) => <p style={{ margin: '0 0 6px 0', lineHeight: 1.6 }}>{children}</p>,
  ul: ({ children }) => <ul style={{ margin: '0 0 6px 0', paddingLeft: 18 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '0 0 6px 0', paddingLeft: 18 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
  strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
  code: ({ inline, children }) =>
    inline ? (
      <code style={{ background: 'rgba(0,0,0,0.08)', padding: '1px 4px', borderRadius: 4, fontSize: '0.92em' }}>{children}</code>
    ) : (
      <code style={{ display: 'block', background: 'rgba(0,0,0,0.06)', padding: '6px 8px', borderRadius: 4, fontSize: 12, overflow: 'auto', marginTop: 4 }}>{children}</code>
    ),
};

/* ── Message Bubble ── */
const Bubble = ({ msg, onConfirmPlan, onModifyPlan, planConfirming }) => {
  const isUser = msg.role === 'user';

  if (msg.type === 'plan') {
    return (
      <div style={styles.planBubbleWrap}>
        <BuildAppPlanCard
          plan={msg.plan}
          onConfirm={() => onConfirmPlan(msg.plan)}
          onModify={onModifyPlan}
          confirming={planConfirming}
        />
      </div>
    );
  }

  if (msg.type === 'building') {
    return (
      <div style={styles.buildingBubble}>
        <LoadingOutlined spin style={{ color: '#7234CF', marginRight: 8 }} />
        <span style={{ color: '#444', fontSize: 14 }}>{msg.content}</span>
        {msg.progress != null && (
          <Progress percent={msg.progress} size="small" style={{ marginTop: 8 }} />
        )}
      </div>
    );
  }

  if (msg.type === 'complete') {
    return (
      <div style={styles.completeBubble}>
        <CheckCircleFilled style={{ color: '#52c41a', fontSize: 20, marginRight: 8 }} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{msg.content}</div>
          {msg.actions && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {msg.actions.map((a, i) => (
                <Button key={i} type="primary" size="small" onClick={a.onClick} style={{ borderRadius: 6 }}>
                  {a.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={isUser ? styles.userBubbleWrap : styles.aiBubbleWrap}>
      <div style={isUser ? styles.userBubble : styles.aiBubble}>
        {msg.imagePreview && (
          <img src={msg.imagePreview} alt="" style={styles.thumbImg} />
        )}
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
          {msg.content || ''}
        </ReactMarkdown>
      </div>
    </div>
  );
};

/* ── Main Component ── */
const BuildAppChat = ({
  messages,
  suggestions,
  detectedType,
  stage,
  sending,
  planConfirming,
  onSend,
  onSuggestion,
  onConfirmPlan,
  onModifyPlan,
  onNewApp,
  onOpenHistory,
}) => {
  const { t } = useLanguage();
  const [inputText, setInputText] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(() => {
    const trimmed = inputText.trim();
    if ((!trimmed && !uploadedImage) || sending) return;
    onSend({
      userMessage: trimmed,
      imageBase64: uploadedImage?.base64 || null,
      imageType: uploadedImage?.type || null,
    });
    setInputText('');
    setUploadedImage(null);
    setImagePreview(null);
  }, [inputText, uploadedImage, sending, onSend]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      antMessage.warning('只支援圖片格式');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      antMessage.warning('圖片最大 5 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      const base64 = dataUrl.split(',')[1];
      setUploadedImage({ base64, type: file.type });
      setImagePreview(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => {
    handleImageFile(e.target.files?.[0]);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleImageFile(e.dataTransfer.files?.[0]);
  };

  const canInput = stage !== 'building' && stage !== 'complete';
  const typeInfo = detectedType ? (PLAN_TYPE_CONFIG[detectedType] || null) : null;

  return (
    <div style={styles.chatOuter}>
      {/* ── Header ── */}
      <div style={styles.header}>
        <span style={styles.headerTitle}>{label(t, 'buildApp.chatTitle', 'Build App')}</span>
        {typeInfo && (
          <Tag color={typeInfo.color} icon={typeInfo.icon} style={{ marginLeft: 8, fontWeight: 600 }}>
            {typeInfo.label}
          </Tag>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={onOpenHistory}
            style={{ borderRadius: 6 }}
          >
            {label(t, 'buildApp.appsHistory', 'Apps 歷史')}
          </Button>
          <Button size="small" icon={<PlusOutlined />} onClick={onNewApp} style={{ borderRadius: 6 }}>
            {label(t, 'buildApp.newApp', '新建應用')}
          </Button>
        </div>
      </div>

      {/* ── Message List ── */}
      <div
        style={styles.messageList}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        {messages.map((msg, idx) => (
          <Bubble
            key={idx}
            msg={msg}
            onConfirmPlan={onConfirmPlan}
            onModifyPlan={onModifyPlan}
            planConfirming={planConfirming}
          />
        ))}
        {sending && (
          <div style={styles.aiBubbleWrap}>
            <div style={styles.aiBubble}>
              <Spin size="small" style={{ marginRight: 6 }} />
              <span style={{ color: '#888', fontSize: 13 }}>{label(t, 'buildApp.aiThinking', 'AI 思考中…')}</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* ── Suggestion Chips ── */}
      {suggestions.length > 0 && canInput && (
        <div style={styles.suggestionsBar}>
          {suggestions.map((s, i) => (
            <button
              key={i}
              style={styles.suggChip}
              onClick={() => {
                onSuggestion(s);
                textareaRef.current?.focus();
              }}
              disabled={sending}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* ── Input Area ── */}
      {canInput && (
        <div style={styles.inputArea}>
          {imagePreview && (
            <div style={styles.imagePreviewWrap}>
              <img src={imagePreview} alt="" style={styles.previewImg} />
              <button
                style={styles.removeImgBtn}
                onClick={() => { setUploadedImage(null); setImagePreview(null); }}
              >
                <DeleteOutlined />
              </button>
            </div>
          )}
          <div style={styles.inputRow}>
            <button
              style={styles.attachBtn}
              onClick={() => fileInputRef.current?.click()}
              title={label(t, 'buildApp.uploadImage', '上傳參考圖片')}
              disabled={sending}
            >
              <PaperClipOutlined />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_IMAGES}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <textarea
              ref={textareaRef}
              style={styles.textarea}
              placeholder={label(t, 'buildApp.inputPlaceholder2', '輸入回覆（Enter 送出，Shift+Enter 換行）')}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              disabled={sending}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={sending}
              disabled={!inputText.trim() && !uploadedImage}
              style={styles.sendBtn}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  chatOuter: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: '#f8f5ff',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 20px',
    background: '#fff',
    borderBottom: '1px solid #ede9fe',
    boxShadow: '0 1px 4px rgba(114,52,207,0.06)',
    zIndex: 1,
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 16,
    color: '#1a1a2e',
  },
  messageList: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 20px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  userBubbleWrap: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  aiBubbleWrap: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  userBubble: {
    background: 'linear-gradient(135deg, #7234CF, #9b5de5)',
    color: '#fff',
    borderRadius: '16px 16px 4px 16px',
    padding: '10px 14px',
    maxWidth: '72%',
    fontSize: 14,
    lineHeight: 1.6,
    wordBreak: 'break-word',
  },
  aiBubble: {
    background: '#fff',
    border: '1px solid #ede9fe',
    borderRadius: '16px 16px 16px 4px',
    padding: '10px 14px',
    maxWidth: '80%',
    fontSize: 14,
    lineHeight: 1.6,
    wordBreak: 'break-word',
    boxShadow: '0 1px 4px rgba(114,52,207,0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  thumbImg: {
    maxWidth: 180,
    maxHeight: 120,
    borderRadius: 6,
    marginBottom: 6,
    objectFit: 'contain',
  },
  planBubbleWrap: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
  },
  buildingBubble: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    background: '#faf5ff',
    border: '1px solid #d8b4fe',
    borderRadius: 12,
    padding: '12px 16px',
    maxWidth: '80%',
    fontSize: 14,
  },
  completeBubble: {
    display: 'flex',
    alignItems: 'flex-start',
    background: '#f6ffed',
    border: '1px solid #b7eb8f',
    borderRadius: 12,
    padding: '14px 16px',
    maxWidth: '80%',
    fontSize: 14,
  },
  suggestionsBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    padding: '8px 20px 6px',
    background: '#fff',
    borderTop: '1px solid #ede9fe',
  },
  suggChip: {
    padding: '4px 12px',
    borderRadius: 16,
    border: '1.5px solid #d8b4fe',
    background: '#faf5ff',
    color: '#5b21b6',
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: 'inherit',
    lineHeight: 1.5,
    transition: 'border-color 0.15s',
  },
  inputArea: {
    padding: '10px 16px 14px',
    background: '#fff',
    borderTop: '1px solid #ede9fe',
  },
  imagePreviewWrap: {
    position: 'relative',
    display: 'inline-block',
    marginBottom: 8,
  },
  previewImg: {
    maxHeight: 80,
    maxWidth: 160,
    borderRadius: 6,
    objectFit: 'contain',
    border: '1px solid #e9d5ff',
  },
  removeImgBtn: {
    position: 'absolute',
    top: -6,
    right: -6,
    background: '#ff4d4f',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    width: 18,
    height: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    cursor: 'pointer',
    padding: 0,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
  },
  attachBtn: {
    background: 'none',
    border: '1px solid #d8b4fe',
    borderRadius: 8,
    padding: '6px 8px',
    cursor: 'pointer',
    fontSize: 16,
    color: '#7234CF',
    lineHeight: 1,
    flexShrink: 0,
    height: 36,
    display: 'flex',
    alignItems: 'center',
  },
  textarea: {
    flex: 1,
    border: '1px solid #d8b4fe',
    borderRadius: 10,
    padding: '7px 12px',
    fontSize: 14,
    fontFamily: 'inherit',
    resize: 'none',
    outline: 'none',
    lineHeight: 1.6,
    background: '#faf5ff',
  },
  sendBtn: {
    borderRadius: 8,
    flexShrink: 0,
    height: 36,
  },
};

export default BuildAppChat;
